import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input } from '@angular/core';
import { TooltipDirective } from '../tooltip/tooltip.directive';

/** `empty` is the unfilled remainder: not drawn (the track shows through), but listed in legend and tooltip. */
export type ZagStackedBarTone = 'neutral' | 'success' | 'warning' | 'info' | 'error' | 'brand' | 'empty';

export type ZagStackedBarPattern = 'solid' | 'hatched';

export interface ZagStackedBarSegment {
  key: string;
  label: string;
  value: number;
  tone: ZagStackedBarTone;
  /** Hatched segments stay distinguishable without colour. */
  pattern?: ZagStackedBarPattern;
  /** Pre-formatted value for legend and tooltip; falls back to the raw number. */
  valueLabel?: string;
}

interface StackedBarItem {
  key: string;
  tone: ZagStackedBarTone;
  hatched: boolean;
  percent: number;
  text: string;
}

/** Proportional multi-segment bar with a legend that doubles as its text alternative. */
@Component({
  selector: 'zag-stacked-bar',
  imports: [TooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'zag-stacked-bar' },
  template: `
    <div class="zag-stacked-bar__track" aria-hidden="true" [zagTooltip]="tooltip()">
      @for (item of items(); track item.key) {
        @if (item.tone !== 'empty') {
          <span
            class="zag-stacked-bar__segment"
            [class]="'zag-stacked-bar__tone--' + item.tone"
            [class.zag-stacked-bar__segment--hatched]="item.hatched"
            [style.width.%]="item.percent"
          ></span>
        }
      }
    </div>
    <ul class="zag-stacked-bar__legend" [class.zag-sr-only]="!showLegend()" [attr.aria-label]="ariaLabel()">
      @for (item of items(); track item.key) {
        <li class="zag-stacked-bar__legend-item">
          <span
            class="zag-stacked-bar__swatch"
            [class]="'zag-stacked-bar__tone--' + item.tone"
            [class.zag-stacked-bar__swatch--hatched]="item.hatched"
            aria-hidden="true"
          ></span>
          {{ item.text }}
        </li>
      }
    </ul>
  `,
  styleUrl: './stacked-bar.component.scss',
})
export class ZagStackedBar {
  readonly segments = input.required<ZagStackedBarSegment[]>();
  /** The whole the bar is measured against; the unfilled remainder shows as track. Defaults to the sum of the segments. */
  readonly total = input<number | null>(null);
  readonly showLegend = input(true, { transform: booleanAttribute });
  /** Accessible name of the legend list, which carries the values for assistive tech. */
  readonly ariaLabel = input.required<string>();

  protected readonly items = computed<StackedBarItem[]>(() => {
    const segments = this.segments();
    const values = segments.map(segment => Math.max(0, segment.value));
    const denominator = Math.max(
      this.total() ?? 0,
      values.reduce((sum, value) => sum + value, 0)
    );
    return segments.map((segment, index) => ({
      key: segment.key,
      tone: segment.tone,
      hatched: segment.pattern === 'hatched',
      percent: denominator > 0 ? (values[index] / denominator) * 100 : 0,
      text: `${segment.label} ${segment.valueLabel ?? segment.value}`,
    }));
  });

  protected readonly tooltip = computed(() =>
    this.items()
      .map(item => item.text)
      .join('\n')
  );
}
